import { Cms, Id, Lookbook, Pagination, ProductFilters } from '@typings';

import { DEFAULT_PRODUCTS_PER_REQUEST } from '../constants/limits';
import { isDefined } from '../utils/is';

import { showroomApi } from './api';

export const fetchLookbooks = async (page: number, limit: number) =>
  showroomApi.QUERY_POST('/lookbooks/filter')({
    data: {
      limit,
      page,
    },
  });

export const createLookbook = async (data: Lookbook.CreateLookbookDTO) =>
  showroomApi.QUERY_POST('/lookbooks')({
    data: {
      ...data,
      linkUri: 'lookbook',
    },
  });

export const createWishlist = async (lookbookId: string, data: Lookbook.CreateWishlistFromLookbookDTO) =>
  showroomApi.QUERY_POST('/lookbooks/:id/wishlist')({
    data,
    pathParams: { id: lookbookId },
  });

export const createAccountRequest = async (lookbookId: string, data: Lookbook.CreateAccountRequestDTO) =>
  showroomApi.QUERY_POST('/lookbooks/:id/accounts/requests')({
    data,
    pathParams: { id: lookbookId },
  });

export const createSelectionFromWishlist = async (data: Lookbook.CreateSelectionFromWishlistDTO) =>
  showroomApi.QUERY_POST('/selections/create-with-products')({
    data
  });

export const fetchGuestWishlist = async (lookbookId: string, wishlistId: string) =>
  showroomApi.QUERY_GET('/lookbooks/:id/wishlist/:wishlistId')({
    pathParams: { id: lookbookId, wishlistId },
  });

export const updateWishlist = async (lookbookId: string, wishlistId: string, data: Lookbook.UpdateWishlistDTO) =>
  showroomApi.QUERY_PUT('/lookbooks/:id/wishlist/:wishlistId')({
    data,
    pathParams: { id: lookbookId, wishlistId },
  });

export const updateLookbookPages = async (lookbookId: string, data: Lookbook.UpdateLookbookPagesDTO) =>
  showroomApi.QUERY_PUT('/lookbooks/:id/navigation')({
    data,
    pathParams: { id: lookbookId },
  });

export const deleteLookbook = async (id: string) =>
  showroomApi.DELETE('/lookbooks/:id')({
    pathParams: {
      id,
    },
  });

export const fetchLookbookById = async (lookbookId: string) =>
  showroomApi.QUERY_GET('/lookbooks/:id')({
    pathParams: { id: lookbookId },
  });

export const fetchLookbookPageBySlug = async (
  lookbookId: string,
  slug: string,
): Promise<
  Cms.SectionsResponseAnyVersion & {
    pageTitle: string;
    textStyles: Cms.TextStyle[];
    buttonStyles: Cms.ButtonStyle[];
  }
> =>
  showroomApi.QUERY_GET('/lookbooks/:id/pages/:slug')({
    pathParams: { id: lookbookId, slug },
  });

export const fetchSingleLookbookProduct = async (productId: Id, lookbookId: string, pricelistId?: string) =>
  showroomApi.QUERY_POST('/lookbooks/:id/products/:productId')({
    data: {
      ...(isDefined(pricelistId) && { pricelistId }),
    },

    pathParams: { id: lookbookId, productId },
  });

export const fetchLookbookProducts = async (lookbookId: string, filters: ProductFilters, pagination: Pagination) => {
  return showroomApi.QUERY_POST('/lookbooks/:id/products')({
    data: {
      allVariants: true,
      limit: pagination.limit || DEFAULT_PRODUCTS_PER_REQUEST, // limit 0 should return default
      skipFirst: pagination.offset,
      ...filters,
    },

    pathParams: {
      id: lookbookId,
    },
  });
};

export const fetchLookbookProductsByProductIds = async (lookbookId: string, productIds: string[], pagination: Pagination) => {
  return showroomApi.QUERY_POST('/lookbooks/:id/products')({
    data: {
      allVariants: true,
      limit: pagination.limit || DEFAULT_PRODUCTS_PER_REQUEST,

      products: productIds,
      // limit 0 should return default
      skipFirst: pagination.offset,
    },

    pathParams: {
      id: lookbookId,
    },
  });
};

export const fetchLookbookPages = async (lookbookId: string) => {
  return showroomApi.QUERY_GET('/lookbooks/:id/pages')({
    pathParams: {
      id: lookbookId,
    },
  });
};
